import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Percent, Save, AlertTriangle, Database, Loader2, MessageSquare, Heart, RotateCcw } from "lucide-react";

export default function Configuracoes() {
  const [cfg, setCfg] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [novaSenhaMaster, setNovaSenhaMaster] = useState("");
  const [desconto, setDesconto] = useState(0);
  const [senhasUnidade, setSenhasUnidade] = useState({});

  // Setup inicial (one-shot)
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupSenha, setSetupSenha] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupResult, setSetupResult] = useState(null);

  // WhatsApp template + status
  const [wppTpl, setWppTpl] = useState({
    template: "",
    ativo: true,
    horario_envio: "14:00",
  });
  const [wppStatus, setWppStatus] = useState(null);

  // Template da Retenção (link wa.me)
  const [retTpl, setRetTpl] = useState({ template: "", default: "" });

  useEffect(() => {
    api.get("/whatsapp/template-pos-venda").then((r) => setWppTpl(r.data));
    api.get("/whatsapp/status").then((r) => setWppStatus(r.data));
    api.get("/whatsapp/template-retencao").then((r) => setRetTpl(r.data));
  }, []);

  const salvarTemplate = async () => {
    if (!wppTpl.template.trim()) {
      toast.error("Template não pode estar vazio.");
      return;
    }
    try {
      await api.put("/whatsapp/template-pos-venda", wppTpl);
      toast.success("Template salvo. Próximo envio usará a nova mensagem.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar template.");
    }
  };

  const dispararAgora = async () => {
    try {
      await api.post("/whatsapp/disparar-pos-venda-agora");
      toast.success("Job pós-venda disparado. Veja logs em alguns instantes.");
    } catch (e) {
      toast.error("Falha ao disparar.");
    }
  };

  const salvarTemplateRetencao = async () => {
    if (!retTpl.template.trim()) {
      toast.error("O template de retenção não pode estar vazio.");
      return;
    }
    try {
      await api.put("/whatsapp/template-retencao", { template: retTpl.template });
      toast.success("Template de retenção salvo. Novos links usarão a mensagem atualizada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar template.");
    }
  };

  const restaurarTemplateRetencao = () => {
    setRetTpl((t) => ({ ...t, template: t.default || "" }));
    toast.message("Texto padrão restaurado. Clique em Salvar para aplicar.");
  };

  const executarSetup = async () => {
    if (!/^\d{4,}$/.test(setupSenha)) {
      toast.error("Digite a senha master atual");
      return;
    }
    setSetupBusy(true);
    setSetupResult(null);
    try {
      const r = await api.post("/admin/setup-inicial", {
        senha_master: setupSenha,
        limpar_historico: true,
        importar_produtos: true,
        apagar_seeds_antigos: true,
      });
      setSetupResult(r.data);
      toast.success("Setup concluído! Confira os números abaixo.");
      setSetupSenha("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no setup");
    } finally {
      setSetupBusy(false);
    }
  };

  const load = async () => {
    const res = await api.get("/configuracoes/full");
    setCfg(res.data.configuracoes);
    setUnidades(res.data.unidades);
    setDesconto(res.data.configuracoes.percentual_desconto_teto);
    setNovaSenhaMaster("");
    const sm = {};
    res.data.unidades.forEach((u) => (sm[u.id] = ""));
    setSenhasUnidade(sm);
  };
  useEffect(() => {
    load();
  }, []);

  const salvarDesconto = async () => {
    await api.put("/configuracoes", { percentual_desconto_teto: Number(desconto) });
    toast.success("Teto de desconto atualizado");
    load();
  };

  const salvarSenhaMaster = async () => {
    if (!/^\d{4,}$/.test(novaSenhaMaster)) {
      toast.error("Senha master deve ter no mínimo 4 dígitos numéricos");
      return;
    }
    await api.put("/configuracoes", { senha_master: novaSenhaMaster });
    toast.success("Senha Master atualizada");
    setNovaSenhaMaster("");
  };

  const salvarSenhaUnidade = async (unidadeId) => {
    const s = senhasUnidade[unidadeId];
    if (!/^\d{4,}$/.test(s)) {
      toast.error("Senha deve ter no mínimo 4 dígitos numéricos");
      return;
    }
    await api.put(`/unidades/${unidadeId}/senha`, { senha_acesso: s });
    toast.success("Senha da unidade atualizada");
    setSenhasUnidade({ ...senhasUnidade, [unidadeId]: "" });
  };

  if (!cfg) return <div className="text-sm text-neutral-500">Carregando...</div>;

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Regras e Segurança
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Configurações
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white border-2 border-neutral-900 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
              <Percent className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
            </div>
            <h3 className="font-display font-black text-xl tracking-tight">
              Desconto Máximo
            </h3>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Limite percentual aplicado em vendas. Operadores não podem ultrapassar.
          </p>
          <div className="flex items-end gap-3">
            <label className="flex-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                % Teto
              </span>
              <input
                data-testid="config-desconto-input"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={desconto}
                onChange={(e) => setDesconto(e.target.value)}
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-lg"
              />
            </label>
            <button
              data-testid="config-desconto-salvar"
              onClick={salvarDesconto}
              className="bg-neutral-950 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Salvar
            </button>
          </div>
          <div className="mt-3 text-xs font-mono text-neutral-500">
            Valor atual: {cfg.percentual_desconto_teto}%
          </div>
        </section>

        <section className="bg-white border-2 border-neutral-900 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-neutral-950 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
            </div>
            <h3 className="font-display font-black text-xl tracking-tight">Senha Master</h3>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Altere a senha numérica de acesso ao Painel Master.
          </p>
          <div className="flex items-end gap-3">
            <label className="flex-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Nova senha (apenas números)
              </span>
              <input
                data-testid="config-senha-master-input"
                type="password"
                value={novaSenhaMaster}
                onChange={(e) => setNovaSenhaMaster(e.target.value.replace(/\D/g, ""))}
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-lg tracking-widest"
                placeholder="••••"
              />
            </label>
            <button
              data-testid="config-senha-master-salvar"
              onClick={salvarSenhaMaster}
              className="bg-neutral-950 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Salvar
            </button>
          </div>
        </section>

        {unidades.map((u) => (
          <section key={u.id} className="bg-white border-2 border-neutral-900 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 flex items-center justify-center"
                style={{ backgroundColor: u.cor_tema === "blue" ? "#2563EB" : "#DC2626" }}
              >
                <KeyRound className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="font-display font-black text-xl tracking-tight">
                Senha — {u.nome}
              </h3>
            </div>
            <div className="flex items-end gap-3">
              <label className="flex-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Nova senha (apenas números)
                </span>
                <input
                  data-testid={`config-senha-${u.slug}-input`}
                  type="password"
                  value={senhasUnidade[u.id] || ""}
                  onChange={(e) =>
                    setSenhasUnidade({
                      ...senhasUnidade,
                      [u.id]: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-lg tracking-widest"
                  placeholder="••••"
                />
              </label>
              <button
                data-testid={`config-senha-${u.slug}-salvar`}
                onClick={() => salvarSenhaUnidade(u.id)}
                className="bg-neutral-950 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Salvar
              </button>
            </div>
          </section>
        ))}
      </div>

      {/* WhatsApp Template */}
      <div className="mt-8 bg-white border-2 border-neutral-900">
        <div className="bg-green-600 text-white p-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-display font-black uppercase tracking-tight">
            WhatsApp · Mensagem automática pós-venda (D+1)
          </h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-neutral-50 border-l-4 border-neutral-900 p-3 font-mono text-xs space-y-1">
            <div>
              <span className="font-bold">Status Z-API:</span>{" "}
              {wppStatus
                ? wppStatus.configurado
                  ? wppStatus.conectado
                    ? "✅ Conectado"
                    : "⚠️ Configurado mas WhatsApp não conectado"
                  : "❌ Não configurado (variáveis ZAPI_* faltando)"
                : "..."}
            </div>
            <div className="text-neutral-500">
              A mensagem é enviada automaticamente um dia após cada venda registrada no
              sistema, no horário configurado abaixo.
            </div>
          </div>

          <L label="Template da mensagem">
            <textarea
              data-testid="config-wpp-template"
              value={wppTpl.template}
              onChange={(e) => setWppTpl({ ...wppTpl, template: e.target.value })}
              rows={5}
              className="mt-1 w-full p-3 border-2 border-neutral-900 font-mono text-sm resize-y"
            />
            <div className="mt-1 font-mono text-[10px] text-neutral-500">
              Placeholders:
              {" "}<code>{"{nome_cliente}"}</code>
              {" "}<code>{"{nome_unidade}"}</code>
              {" "}<code>{"{data_venda}"}</code>
              {" "}<code>{"{modelo_veiculo}"}</code>
              {" "}<code>{"{placa}"}</code>
            </div>
          </L>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <L label="Horário do envio diário (BRT)">
              <input
                data-testid="config-wpp-horario"
                type="time"
                value={wppTpl.horario_envio}
                onChange={(e) => setWppTpl({ ...wppTpl, horario_envio: e.target.value })}
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
              />
            </L>
            <L label="Envio automático">
              <select
                data-testid="config-wpp-ativo"
                value={wppTpl.ativo ? "1" : "0"}
                onChange={(e) =>
                  setWppTpl({ ...wppTpl, ativo: e.target.value === "1" })
                }
                className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono bg-white"
              >
                <option value="1">Ativo</option>
                <option value="0">Desativado</option>
              </select>
            </L>
            <div className="flex items-end gap-2">
              <button
                data-testid="config-wpp-salvar"
                onClick={salvarTemplate}
                className="flex-1 bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 cta-brutal"
              >
                <Save className="w-4 h-4" /> Salvar
              </button>
            </div>
          </div>

          <button
            data-testid="config-wpp-disparar"
            onClick={dispararAgora}
            className="bg-white border-2 border-neutral-900 hover:bg-neutral-100 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-[11px] tracking-widest"
          >
            Disparar job agora (teste)
          </button>
        </div>
      </div>

      {/* WhatsApp Template · Retenção (link wa.me) */}
      <div className="mt-8 bg-white border-2 border-neutral-900">
        <div className="bg-neutral-950 text-yellow-400 p-3 flex items-center gap-2">
          <Heart className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-display font-black uppercase tracking-tight">
            WhatsApp · Mensagem de retenção (link copiável)
          </h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-neutral-50 border-l-4 border-neutral-900 p-3 font-mono text-xs text-neutral-600">
            Esta é a mensagem usada quando você clica em{" "}
            <span className="font-bold">"Copiar link WhatsApp"</span> em{" "}
            <span className="font-bold">Retenção</span>. O link gerado abre uma
            conversa com o cliente já com o texto preenchido.
          </div>

          <L label="Template da mensagem">
            <textarea
              data-testid="config-retencao-template"
              value={retTpl.template}
              onChange={(e) => setRetTpl({ ...retTpl, template: e.target.value })}
              rows={6}
              className="mt-1 w-full p-3 border-2 border-neutral-900 font-mono text-sm resize-y"
            />
            <div className="mt-1 font-mono text-[10px] text-neutral-500">
              Placeholders disponíveis:{" "}
              <code>{"{nome_cliente}"}</code>{" "}
              <code>{"{nome_unidade}"}</code>{" "}
              <code>{"{modelo_veiculo}"}</code>{" "}
              <code>{"{placa}"}</code>{" "}
              <code>{"{data_ultima_troca}"}</code>{" "}
              <code>{"{dias_desde_ultima_troca}"}</code>{" "}
              <code>{"{meses_filtro}"}</code>{" "}
              <code>{"{itens_ultima_compra}"}</code>
            </div>
          </L>

          {/* Preview com dados fictícios */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              Preview (com dados fictícios)
            </div>
            <div className="bg-green-50 border-l-4 border-green-600 p-3 font-mono text-xs whitespace-pre-wrap">
              {renderPreviewRetencao(retTpl.template)}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="config-retencao-salvar"
              onClick={salvarTemplateRetencao}
              className="bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
            >
              <Save className="w-4 h-4" /> Salvar template
            </button>
            <button
              data-testid="config-retencao-restaurar"
              onClick={restaurarTemplateRetencao}
              className="bg-white border-2 border-neutral-900 hover:bg-neutral-100 text-neutral-950 h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Restaurar padrão
            </button>
          </div>
        </div>
      </div>

      {/* Setup inicial / reset one-shot */}
      <div className="mt-8 bg-white border-4 border-red-600">
        <div className="bg-red-600 text-white p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-display font-black uppercase tracking-tight">
            Zona de Risco · Setup Inicial
          </h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm">
            Este botão é uma ação <strong>one-shot</strong> destinada a preparar o sistema
            para uso real. Ela executa, em sequência:
          </p>
          <ul className="text-sm list-disc pl-5 space-y-1 font-mono text-[12px]">
            <li>Apaga TODAS as vendas, fechamentos, metas e clientes (dados de teste)</li>
            <li>Apaga os 10 produtos seed antigos (do DEMO inicial)</li>
            <li>Importa 99 produtos das planilhas oficiais (Santa Clara + Laçador) com custo, preço e quantidade = 1</li>
          </ul>
          <p className="text-xs font-mono text-red-600">
            ⚠ Ação irreversível. Use APENAS no início, antes da primeira operação real.
          </p>

          {!setupOpen && (
            <button
              data-testid="config-setup-abrir"
              onClick={() => setSetupOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
            >
              <Database className="w-4 h-4" /> Executar Setup Inicial
            </button>
          )}

          {setupOpen && (
            <div className="bg-neutral-50 border-2 border-neutral-900 p-4 space-y-3">
              <label className="block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Confirme a senha Master para prosseguir
                </div>
                <input
                  data-testid="config-setup-senha"
                  type="password"
                  value={setupSenha}
                  onChange={(e) => setSetupSenha(e.target.value)}
                  className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-lg tracking-widest"
                  placeholder="••••"
                />
              </label>
              <div className="flex gap-2">
                <button
                  data-testid="config-setup-cancelar"
                  onClick={() => {
                    setSetupOpen(false);
                    setSetupSenha("");
                  }}
                  disabled={setupBusy}
                  className="bg-white text-neutral-950 border-2 border-neutral-900 h-11 px-5 font-display font-bold uppercase text-xs tracking-widest"
                >
                  Cancelar
                </button>
                <button
                  data-testid="config-setup-confirmar"
                  onClick={executarSetup}
                  disabled={setupBusy}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
                >
                  {setupBusy ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Executando...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4" /> Confirmar e Executar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {setupResult && (
            <div
              data-testid="config-setup-resultado"
              className="bg-green-50 border-2 border-green-600 p-4 font-mono text-xs space-y-1"
            >
              <div className="font-display font-black text-green-800 mb-1">
                ✅ Setup concluído
              </div>
              {setupResult.limpou_historico && (
                <div>
                  Histórico limpo:{" "}
                  {Object.entries(setupResult.limpou_historico)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(" · ")}
                </div>
              )}
              {setupResult.apagou_seeds && (
                <div>
                  Seeds antigos apagados: produtos={setupResult.apagou_seeds.produtos}, estoque_linhas=
                  {setupResult.apagou_seeds.estoque_linhas}
                </div>
              )}
              {setupResult.importou && (
                <div>
                  Produtos importados: {setupResult.importou.criados} novos · estoque criado{" "}
                  {setupResult.importou.estoque_criado} · total{" "}
                  {setupResult.importou.total_produtos_apos}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
      {label}
    </span>
    {children}
  </label>
);

const PREVIEW_SAMPLE = {
  "{nome_cliente}": "João da Silva",
  "{nome_unidade}": "Posto Santa Clara",
  "{modelo_veiculo}": "Fiat Argo 2021",
  "{placa}": "ABC1D23",
  "{data_ultima_troca}": "15/09/2025",
  "{dias_desde_ultima_troca}": "180",
  "{meses_filtro}": "6",
  "{itens_ultima_compra}": "1x Óleo 5W30 Sintético, 1x Filtro de Óleo",
};

function renderPreviewRetencao(tpl) {
  let out = tpl || "";
  for (const [k, v] of Object.entries(PREVIEW_SAMPLE)) {
    out = out.split(k).join(v);
  }
  return out || "(digite o template acima)";
}

